import * as React from "react";
import { Info } from "lucide-react";
import { cn } from "@/lib/utils";

export interface OdsInputProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, "prefix" | "size"> {
  action?: React.ReactNode;
  containerClassName?: string;
  error?: React.ReactNode;
  info?: React.ReactNode;
  inputClassName?: string;
  label?: React.ReactNode;
  message?: React.ReactNode;
  prefix?: React.ReactNode;
  requiredIndicator?: boolean;
  suffix?: React.ReactNode;
}

export const OdsInput = React.forwardRef<HTMLInputElement, OdsInputProps>(
  (
    {
      action,
      className,
      containerClassName,
      disabled,
      error,
      id,
      info,
      inputClassName,
      label,
      message,
      prefix,
      requiredIndicator = false,
      suffix,
      ...props
    },
    ref
  ) => {
    const generatedId = React.useId();
    const inputId = id ?? generatedId;
    const helperText = error ?? message;
    const isDestructive = Boolean(error);

    return (
      <div className={cn("flex flex-col gap-2", containerClassName)}>
        {label ? (
          <label
            htmlFor={inputId}
            className="flex items-center gap-1 text-[14px] font-medium leading-[120%] text-[var(--fg-secondary)]"
          >
            <span>{label}</span>
            {requiredIndicator ? <span className="text-[var(--fg-brand)]">*</span> : null}
            {info ? (
              <span title={typeof info === "string" ? info : undefined}>
                <Info className="h-3.5 w-3.5 text-[var(--fg-secondary)]" />
              </span>
            ) : null}
          </label>
        ) : null}

        <div
          className={cn(
            "flex h-10 w-full items-center gap-3 rounded-lg bg-[var(--bg-secondary)] px-3 transition-[border-color,opacity]",
            isDestructive ? "border border-[var(--border-negative)]" : "border border-transparent focus-within:border-[var(--border-primary)]",
            disabled && "opacity-50",
            className
          )}
        >
          {prefix ? <span className="shrink-0 text-[var(--fg-placeholder)]">{prefix}</span> : null}
          <input
            id={inputId}
            ref={ref}
            disabled={disabled}
            aria-invalid={isDestructive || undefined}
            className={cn(
              "min-w-0 flex-1 bg-transparent text-[14px] font-medium leading-[120%] text-[var(--fg-base)] outline-none placeholder:text-[var(--fg-placeholder)] disabled:cursor-not-allowed",
              inputClassName
            )}
            {...props}
          />
          {suffix ? <span className="shrink-0 text-[14px] font-medium leading-[120%] text-[var(--fg-placeholder)]">{suffix}</span> : null}
          {action}
        </div>

        {helperText ? (
          <p className={cn("text-[12px] font-medium leading-[120%]", isDestructive ? "text-[var(--fg-negative)]" : "text-[var(--fg-secondary)]")}>
            {helperText}
          </p>
        ) : null}
      </div>
    );
  }
);

OdsInput.displayName = "OdsInput";
